import type { Tile } from '@schema/tiles.js';

/**
 * Tiny self-contained toast for the marketing-site / browser demo.
 *
 * The kiosk paths (Android bridge, SOTI host) actually launch apps,
 * so failures there are real and surface via the existing tile
 * statusHint. In a regular browser there's no native bridge to call,
 * so app-tile taps "fail" with "requires the SOTI JS Agent API."
 * That error looks broken to a marketing-page visitor.
 *
 * This helper makes the demo feel responsive: a brief pill near the
 * bottom of the viewport that fades in and out. No state, no React
 * component — direct DOM so it works regardless of where it's called
 * from, including inside the launcher's iframe-bezel in DemoShell.
 */

let activeToast: HTMLElement | null = null;
let activeTimer: ReturnType<typeof setTimeout> | null = null;

export function showDemoToast(message: string): void {
  if (typeof document === 'undefined') return;

  // Reuse the same element across rapid taps so we don't pile toasts up.
  if (activeToast === null) {
    activeToast = document.createElement('div');
    activeToast.setAttribute('role', 'status');
    activeToast.setAttribute('aria-live', 'polite');
    Object.assign(activeToast.style, {
      position: 'fixed',
      bottom: '2.5rem',
      left: '50%',
      transform: 'translateX(-50%) translateY(0.5rem)',
      padding: '0.6rem 1.1rem',
      background: 'rgba(11, 12, 15, 0.92)',
      color: '#ffffff',
      borderRadius: '999px',
      fontSize: '0.9rem',
      fontWeight: '500',
      fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)',
      zIndex: '9999',
      opacity: '0',
      pointerEvents: 'none',
      transition: 'opacity 140ms ease-out, transform 140ms ease-out',
      maxWidth: '80vw',
      textAlign: 'center',
    } satisfies Partial<CSSStyleDeclaration>);
    document.body.appendChild(activeToast);
  }

  const el = activeToast;
  el.textContent = message;

  // Reset + animate in.
  if (activeTimer !== null) clearTimeout(activeTimer);
  // requestAnimationFrame so the transition picks up the change.
  requestAnimationFrame(() => {
    el.style.opacity = '1';
    el.style.transform = 'translateX(-50%) translateY(0)';
  });

  activeTimer = setTimeout(() => {
    el.style.opacity = '0';
    el.style.transform = 'translateX(-50%) translateY(0.5rem)';
    activeTimer = setTimeout(() => {
      if (el.parentNode) el.parentNode.removeChild(el);
      activeToast = null;
      activeTimer = null;
    }, 180);
  }, 1800);
}

/**
 * Build the demo-toast text for a tile that would launch something on
 * a real device. Used by App.tsx's tile-activate fallback (when the
 * browser host can't satisfy the dispatch) and by PreviewApp so the
 * Builder live-preview shows the same feedback the live launcher would.
 *
 * Format: "Launcher <label> using <target> via <method> (demo)" — gives
 * admins enough detail to spot a typo in the package name / URL without
 * having to dig into the tile's config editor.
 */
export function friendlyDemoLabel(tile: Tile): string {
  switch (tile.type) {
    case 'app':
      return `Launching ${tile.label} using ${tile.packageName} via Android intent (demo)`;
    case 'web':
      return `Launching ${tile.label} using ${tile.url} via WebView (demo)`;
    case 'agentAction':
      return `Launching ${tile.label} using ${tile.action} via the device MDM agent (demo)`;
    case 'spacer':
      return '';
    case 'divider':
      // Divider tiles don't dispatch — handled at the grid render
      // layer as a non-interactive bar. Unreachable in practice but
      // the exhaustive switch needs the arm.
      return '';
    case 'folder':
      // Folder taps open the FolderModal at the renderer layer, not
      // dispatched through the engine — this branch is unreachable
      // in practice, but the switch needs exhaustive arms.
      return `Opening ${tile.label} folder (demo)`;
  }
}
